import { z } from 'zod'
import {
  benefitAndVoucherManagementSchema,
  type BenefitAndVoucherManagement
} from '@/models/api/benefit-and-voucher-management'
import type { BenefitAndVoucherManagementExcludeItineraryRequest } from '@/models/api/benefit-and-voucher-management-exclude-itinerary-request'
import {
  benefitAndVoucherManagementGetBenefitCountSchema,
  type BenefitAndVoucherManagementGetBenefitCount
} from '@/models/api/benefit-and-voucher-management-get-benefit-count'
import {
  benefitAndVoucherManagementGetFlightSegmentCouponListSchema,
  type BenefitAndVoucherManagementGetFlightSegmentCouponList
} from '@/models/api/benefit-and-voucher-management-get-flight-segment-coupon-list'
import type { BenefitAndVoucherManagementGetFlightSegmentCouponListRequest } from '@/models/api/benefit-and-voucher-management-get-flight-segment-coupon-list-request'
import {
  benefitAndVoucherManagementGetHistoryListSchema,
  type BenefitAndVoucherManagementGetHistoryList
} from '@/models/api/benefit-and-voucher-management-get-history-list'
import type { BenefitAndVoucherManagementGetHistoryListRequest } from '@/models/api/benefit-and-voucher-management-get-history-list-request'
import type { BenefitAndVoucherManagementGetListRequest } from '@/models/api/benefit-and-voucher-management-get-list-request'
import {
  benefitAndVoucherManagementGetMyItineraryListSchema,
  type BenefitAndVoucherManagementGetMyItineraryList
} from '@/models/api/benefit-and-voucher-management-get-my-itinerary-list'

import {
  benefitAndVoucherManagementGetPromoCodeSchema,
  type BenefitAndVoucherManagementGetPromoCode
} from '@/models/api/benefit-and-voucher-management-get-promo-code'
import { benefitAndVoucherManagementGetSelectCourtesyArrangementsSchema } from '@/models/api/benefit-and-voucher-management-get-select-courtesy-arrangements'
import type { BenefitAndVoucherManagementGetSelectCourtesyArrangements } from '@/models/api/benefit-and-voucher-management-get-select-courtesy-arrangements'
import type { BenefitAndVoucherManagementGetSelectCourtesyArrangementsRequest } from '@/models/api/benefit-and-voucher-management-get-select-courtesy-arrangements-request'
import type { BenefitAndVoucherManagementGetUsageRulesFileRequest } from '@/models/api/benefit-and-voucher-management-get-usage-rules-file-request'
import {
  benefitAndVoucherManagementRedeemBenefitsSchema,
  type BenefitAndVoucherManagementRedeemBenefits
} from '@/models/api/benefit-and-voucher-management-redeem-benefits'
import type { BenefitAndVoucherManagementRedeemBenefitsRequest } from '@/models/api/benefit-and-voucher-management-redeem-benefits-request'
import type { GetUsedCourtesyRequest } from '@/models/api/get-used-courtesy-request'
import {
  getUsedCourtesyResponseSchema,
  type GetUsedCourtesyResponse
} from '@/models/api/get-used-courtesy-response'
import { useEnv } from '~library/composables'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class BenefitAndVoucherManagementApi {
  VITE_SHOW_1A_ERROR: boolean | undefined

  constructor(private api: ApiFactory) {
    this.VITE_SHOW_1A_ERROR = useEnv().VITE_SHOW_1A_ERROR
  }

  /**
   * API 取得我的禮遇清單
   * https://hackmd.io/rnHATMWAQHuxE_Tz6Lg8Lg
   */
  @apiValidator(benefitAndVoucherManagementSchema.array())
  getList(
    query?: BenefitAndVoucherManagementGetListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<BenefitAndVoucherManagement[]>> {
    return this.api.post('BenefitAndVoucherManagement/GetList', query, config)
  }

  /**
   * API 取得禮遇歷史紀錄清單
   * https://hackmd.io/EpN63BCERfikCgENX_VUXw
   */
  @apiValidator(benefitAndVoucherManagementGetHistoryListSchema.array())
  getHistoryList(
    query?: BenefitAndVoucherManagementGetHistoryListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<BenefitAndVoucherManagementGetHistoryList[]>> {
    return this.api.post(
      'BenefitAndVoucherManagement/GetHistoryList',
      query,
      config
    )
  }

  /**
   * API 取得我的行程清單
   * https://hackmd.io/GH1xDTYvTA2yj_GcW83cPw
   */
  @apiValidator(benefitAndVoucherManagementGetMyItineraryListSchema.array())
  getMyItineraryList(
    memberCardNumber: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<BenefitAndVoucherManagementGetMyItineraryList[]>> {
    return this.api.get(
      `BenefitAndVoucherManagement/GetMyItineraryList/${memberCardNumber}`,
      config
    )
  }

  /**
   * API 排除行程
   * https://hackmd.io/SZ1xJ3eLRUCyGj3YKd42nA
   */
  @apiValidator(z.string())
  excludeItinerary(
    query: BenefitAndVoucherManagementExcludeItineraryRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.put(
      'BenefitAndVoucherManagement/ExcludeItinerary',
      query,
      config
    )
  }

  /**
   * API 啟用優惠碼
   * https://hackmd.io/vQBMcixzQ0Cum7uugxqbUQ
   */
  @apiValidator(benefitAndVoucherManagementGetPromoCodeSchema.nullable())
  getPromoCode(
    id: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<BenefitAndVoucherManagementGetPromoCode | null>> {
    return this.api.get(
      `BenefitAndVoucherManagement/GetPromoCode/${id}`,
      config
    )
  }

  /**
   * API 取得我的禮遇數量
   * https://hackmd.io/qIfefAnvQQi9jQEukt_13A
   */
  @apiValidator(benefitAndVoucherManagementGetBenefitCountSchema.nullable())
  getListCount(
    memberCardNumber: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<BenefitAndVoucherManagementGetBenefitCount | null>> {
    return this.api.get(
      `BenefitAndVoucherManagement/GetBenefitCount/${memberCardNumber}`,
      config
    )
  }

  /**
   * API 取得禮遇項目使用規則PDF檔案
   * https://hackmd.io/wQm7uw-6QV2YpKvyYPRWpA
   */
  getUsageRulesFile(
    data: BenefitAndVoucherManagementGetUsageRulesFileRequest
  ): Promise<void> {
    return this.api.downloadFile({
      method: 'POST',
      url: 'BenefitAndVoucherManagement/GetUsageRulesFile',
      data: data
    })
  }

  /**
   * API 取得選擇禮遇安排資訊
   * https://hackmd.io/H-Cjg2WVQEqcJGfSPAhMuw
   */
  @apiValidator(benefitAndVoucherManagementGetSelectCourtesyArrangementsSchema)
  getSelectCourtesyArrangements(
    query: BenefitAndVoucherManagementGetSelectCourtesyArrangementsRequest
  ): Promise<
    ApiResponse<BenefitAndVoucherManagementGetSelectCourtesyArrangements>
  > {
    return this.api.post(
      'BenefitAndVoucherManagement/GetSelectCourtesyArrangements',
      query
    )
  }

  /**
   * API 取得航段可使用的禮遇項目清單
   * https://hackmd.io/op9SMNpgQSKFaQXqrepAlg
   */
  @apiValidator(
    benefitAndVoucherManagementGetFlightSegmentCouponListSchema.array()
  )
  getFlightSegmentCouponList(
    query: BenefitAndVoucherManagementGetFlightSegmentCouponListRequest,
    config?: ApiRequestConfig
  ): Promise<
    ApiResponse<BenefitAndVoucherManagementGetFlightSegmentCouponList[]>
  > {
    return this.api.post(
      'BenefitAndVoucherManagement/GetFlightSegmentCouponList',
      query,
      config
    )
  }

  /**
   * API 禮遇安排兌換
   * https://hackmd.io/DM7JaDpLQzyAobE5YvbOCg
   */
  @apiValidator(benefitAndVoucherManagementRedeemBenefitsSchema)
  redeemBenefits(
    query: BenefitAndVoucherManagementRedeemBenefitsRequest
  ): Promise<ApiResponse<BenefitAndVoucherManagementRedeemBenefits>> {
    return this.api.post('BenefitAndVoucherManagement/RedeemBenefits', query, {
      headers: {
        disableAlert: !this.VITE_SHOW_1A_ERROR,
        ignoreRtnCodeError: ['68']
      }
    })
  }

  /**
   * API 取得行程之已使用禮遇項目
   * https://hackmd.io/CzMJb6suTOCgMskcBvmhHg
   */
  @apiValidator(getUsedCourtesyResponseSchema.array())
  getUsedCourtesy(
    query: GetUsedCourtesyRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<GetUsedCourtesyResponse[]>> {
    return this.api.post(
      'BenefitAndVoucherManagement/GetUsedCourtesy',
      query,
      config
    )
  }
}
